"use client";
import { useLocale, useTranslations } from "next-intl";
import {
  AlertCircle,
  ArrowUpRight,
  CheckCircle2,
  ChevronDown,
  Info,
  SearchX,
} from "lucide-react";
import type { EnergyLabelView } from "@/lib/energy-label/view";
import { cn } from "@/lib/utils";
import EnergyLabelLadder from "./EnergyLabelLadder";
import { getEnergyLabelColors, getEnergyLabelIndex } from "./energy-label-scale";

/**
 * One finder answer: the registered class with its dates and validity, the
 * class marked on the official ladder, and the assessment details behind it.
 * Not-found and ambiguous answers explain what to ask the landlord instead.
 */
export default function EnergyLabelResult({ data }: { data: EnergyLabelView }) {
  const t = useTranslations("energy-label");
  const locale = useLocale();
  const date = (value: string | null) =>
    value
      ? new Intl.DateTimeFormat(locale, {
          day: "numeric",
          month: "short",
          year: "numeric",
          timeZone: "UTC",
        }).format(new Date(value))
      : t("unknown");
  const found = data.state === "found";
  const hasIndicators = Object.values(data.indicators).some((v) => v !== null);

  return (
    <article className="flex min-w-0 flex-col gap-5">
      <div className="flex items-center gap-4">
        {found ? (
          <span
            className="flex h-16 min-w-16 flex-none items-center justify-center rounded-2xl px-3 text-2xl font-bold tracking-tight"
            style={getEnergyLabelColors(data.label)}
          >
            {data.label}
          </span>
        ) : (
          <span className="flex size-14 flex-none items-center justify-center rounded-2xl bg-muted text-muted-foreground">
            <SearchX className="size-6" aria-hidden="true" />
          </span>
        )}
        <div className="flex min-w-0 flex-col gap-1">
          <h3 className="text-lg font-semibold leading-snug">
            {t(
              found
                ? "result-title"
                : data.state === "ambiguous"
                  ? "ambiguous-title"
                  : "not-found-title",
            )}
          </h3>
          <p className="break-words text-sm text-muted-foreground">
            {data.address}
          </p>
          {found && (
            <span
              className={cn(
                "inline-flex w-fit items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold",
                data.expired
                  ? "bg-amber-100 text-amber-900"
                  : "bg-emerald-50 text-emerald-800",
              )}
            >
              {data.expired ? (
                <AlertCircle className="size-3.5" aria-hidden="true" />
              ) : (
                <CheckCircle2 className="size-3.5" aria-hidden="true" />
              )}
              {t(data.expired ? "status-expired" : "status-valid")}
            </span>
          )}
        </div>
      </div>

      {!found && (
        <p className="text-sm leading-6 text-muted-foreground">
          {t(data.state === "ambiguous" ? "ambiguous-description" : "not-found")}
        </p>
      )}

      {found && (
        <dl className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {(
            [
              ["registered", data.registeredAt],
              ["valid", data.validUntil],
              ["checked", data.checkedAt],
            ] as const
          ).map(([key, value]) => (
            <div
              key={key}
              className="flex min-w-0 flex-col gap-0.5 rounded-xl bg-slate-50 px-3 py-2"
            >
              <dt className="text-[11px] font-medium text-muted-foreground">
                {t(key)}
              </dt>
              <dd className="text-sm font-semibold">{date(value)}</dd>
            </div>
          ))}
        </dl>
      )}

      {(data.stale || data.expired) && (
        <p className="flex items-start gap-2 rounded-xl bg-amber-50 p-3 text-sm leading-6 text-amber-900">
          <AlertCircle className="mt-1 size-4 flex-none" aria-hidden="true" />
          {t(data.expired ? "expired" : "stale")}
        </p>
      )}

      {found && (
        <EnergyLabelLadder
          activeIndex={getEnergyLabelIndex(data.label)}
          activeValue={data.indicators.fossil}
        />
      )}

      <p className="flex items-start gap-2 text-xs leading-5 text-muted-foreground">
        <Info className="mt-0.5 size-3.5 flex-none" aria-hidden="true" />
        {t("bill-note")}
      </p>

      {found && hasIndicators && (
        <details className="group rounded-xl border border-border">
          <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between gap-3 px-4 text-sm font-semibold [&::-webkit-details-marker]:hidden">
            {t("technical")}
            <ChevronDown
              className="size-4 transition-transform group-open:rotate-180"
              aria-hidden="true"
            />
          </summary>
          <dl className="flex flex-col divide-y divide-border border-t border-border px-4 text-sm">
            {(["fossil", "demand", "renewable"] as const).map(
              (key) =>
                data.indicators[key] !== null && (
                  <div
                    key={key}
                    className="flex flex-wrap justify-between gap-2 py-2.5"
                  >
                    <dt className="text-muted-foreground">{t(key)}</dt>
                    <dd className="font-semibold tabular-nums">
                      {new Intl.NumberFormat(locale, {
                        maximumFractionDigits: 1,
                      }).format(data.indicators[key]!)}{" "}
                      {key === "renewable" ? "%" : `kWh/m²/${t("per-year")}`}
                    </dd>
                  </div>
                ),
            )}
          </dl>
          <p className="border-t border-border px-4 py-3 text-xs leading-5 text-muted-foreground">
            {t("technical-note")}
          </p>
        </details>
      )}

      <a
        href="https://www.ep-online.nl/"
        target="_blank"
        rel="noopener noreferrer"
        title={t("official-link-title")}
        className="inline-flex min-h-8 w-fit items-center gap-1 text-xs font-semibold text-blue-700 hover:underline"
      >
        {t("official-link")}
        <ArrowUpRight className="size-3.5" aria-hidden="true" />
      </a>
    </article>
  );
}
